import { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { IconPulse, IconRefresh } from '../components/icons';
import { CodeTabs, Empty, Skeleton, useAsync } from '../components/ui';
import { api, ContactEvent, Paged } from '../lib/api';
import { ago, dateTime, num } from '../lib/format';
import { t } from '../lib/i18n';

export function eventSnippets(key = 'mo_test_…') {
  const host = window.location.origin;
  return {
    cURL: `curl -X POST ${host}/v1/events \\
  -H "Authorization: Bearer ${key}" \\
  -H "Content-Type: application/json" \\
  -d '{
    "name": "order.paid",
    "contact": { "external_id": "u_42" },
    "properties": { "order_id": "1042", "total": 2490 },
    "idempotency_key": "order-1042-paid"
  }'`,
    'Node.js': `${t('// Без idempotencyKey SDK сам ставит ключ — повтор при сбое сети не создаст второе событие.')}
await mo.events.track({
  name: 'order.paid',
  contact: { external_id: user.id },
  properties: { order_id: order.id, total: order.total },
  idempotencyKey: \`order-\${order.id}-paid\`,
});`,
    Python: `mo.events.track(
    "order.paid",
    contact={"external_id": user.id},
    properties={"order_id": order.id, "total": order.total},
    idempotency_key=f"order-{order.id}-paid",
)`,
    Go: `_, err := mo.Events.Track(ctx, mailocean.Event{
	Name:           "order.paid",
	Contact:        mailocean.ContactRef{ExternalID: user.ID},
	Properties:     map[string]any{"order_id": order.ID, "total": order.Total},
	IdempotencyKey: "order-" + order.ID + "-paid",
})`,
  };
}

export function EventsPage() {
  const [params, setParams] = useSearchParams();
  const name = params.get('name') ?? '';
  const top = useAsync(() => api.get<{ name: string; count: number; last_at: string | null }[]>('/events/names'));
  const [items, setItems] = useState<ContactEvent[]>([]);
  const [cursor, setCursor] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  async function load(reset = true) {
    setLoading(true);
    try {
      const p = new URLSearchParams({ limit: '50' });
      if (name) p.set('name', name);
      if (!reset && cursor) p.set('cursor', cursor);
      const res = await api.get<Paged<ContactEvent>>(`/events?${p}`);
      setItems((xs) => (reset ? res.items : [...xs, ...res.items]));
      setCursor(res.next_cursor);
    } finally {
      setLoading(false);
    }
  }
  useEffect(() => {
    void load(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [name]);
  const pick = (n: string) => setParams(n ? { name: n } : {});

  return (
    <>
      <div className="page-head">
        <div className="grow">
          <h1>{t('События')}</h1>
          <p>{t('Что делают ваши контакты: заказы, корзины, входы. По событиям строятся сегменты и сценарии. Повтор с тем же')} <code>idempotency_key</code> {t('не создаёт второе событие.')}</p>
        </div>
        <button className="btn" onClick={() => (load(true), top.reload())}><IconRefresh size={15} /> {t('Обновить')}</button>
      </div>
      <div className="grid grid-2">
        <div className="card" style={{ minWidth: 0 }}>
          <div className="card-pad chips" style={{ borderBottom: '1px solid var(--border)' }}>
            <button className={`chip${!name ? ' on' : ''}`} onClick={() => pick('')}>{t('Все')}</button>
            {top.data?.map((t) => (
              <button key={t.name} className={`chip${name === t.name ? ' on' : ''}`} onClick={() => pick(t.name)}>{t.name}</button>
            ))}
          </div>
          {loading && !items.length ? (
            <div className="card-pad stack">{Array.from({ length: 5 }, (_, i) => <Skeleton key={i} />)}</div>
          ) : !items.length ? (
            <Empty icon={<IconPulse size={36} />} title={name ? t('Таких событий нет') : t('Событий пока нет')}>{t('Передайте первое событие из приложения — пример справа.')}</Empty>
          ) : (
            <div className="stack" style={{ gap: 0 }}>
              {items.map((e) => (
                <details key={e.id} className="card-pad" style={{ borderTop: '1px solid var(--border)' }}>
                  <summary className="row wrap" style={{ cursor: 'pointer', gap: 8 }}>
                    <code>{e.name}</code>
                    <Link to={`/contacts/${e.contact_id}`} className="small" onClick={(ev) => ev.stopPropagation()}>{t('контакт')}</Link>
                    <span className="spacer" />
                    <span className="small muted" title={dateTime(e.occurred_at)}>{ago(e.occurred_at)}</span>
                  </summary>
                  <pre className="code" style={{ marginTop: 8 }}>{JSON.stringify(e.properties, null, 2)}</pre>
                  <div className="small muted">
                    {e.idempotency_key ? <>{t('ключ')} <code>{e.idempotency_key}</code> · </> : `${t('без ключа')} · `}
                    {t('принято {d}', { d: dateTime(e.received_at) })}
                  </div>
                </details>
              ))}
            </div>
          )}
          {cursor && (
            <div className="card-pad" style={{ textAlign: 'center', borderTop: '1px solid var(--border)' }}>
              <button className="btn" onClick={() => load(false)} disabled={loading}>{loading ? t('Загружаем…') : t('Загрузить ещё')}</button>
            </div>
          )}
        </div>
        <div className="stack" style={{ minWidth: 0 }}>
          <div className="card">
            <div className="card-head"><h3>{t('Топ за 7 дней')}</h3></div>
            {!top.data?.length ? (
              <div className="card-pad small muted">{t('За неделю событий не было.')}</div>
            ) : (
              <table className="table">
                <tbody>
                  {top.data.map((t) => (
                    <tr key={t.name} className="click" onClick={() => pick(t.name)}>
                      <td className="truncate" style={{ maxWidth: 200 }}><code>{t.name}</code></td>
                      <td style={{ textAlign: 'right' }}><strong>{num(t.count)}</strong></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
          <div className="card card-pad stack" style={{ minWidth: 0 }}>
            <h3>{t('Как передать событие')}</h3>
            <CodeTabs snippets={eventSnippets()} />
          </div>
        </div>
      </div>
    </>
  );
}
